import { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import clsx from "clsx";
import { Menu, X, ArrowRight, Zap, ChevronDown } from "lucide-react";

const AI_FEATURES = [
  { to: "/ai-campaign-builder",       label: "Campaign Builder",          desc: "Write full campaigns from one sentence" },
  { to: "/ai-campaign-generator",     label: "AI Campaign Generator",     desc: "Multi-step sequences from a single prompt" },
  { to: "/subject-line-optimisation", label: "Subject Line Optimisation", desc: "A/B test up to 4 AI-written variants"   },
  { to: "/auto-optimization",         label: "Auto-Optimization",         desc: "AI resends to non-openers automatically" },
  { to: "/send-time-optimization",    label: "Send-Time Optimization",    desc: "Deliver when each subscriber is most active" },
  { to: "/spam-score",                label: "Spam Score",                desc: "1–10 risk score before every send"      },
  { to: "/inbox-preview",             label: "Inbox Preview",             desc: "Gmail, Outlook & dark mode rendering"   },
  { to: "/engagement-scoring",        label: "Engagement Scoring",        desc: "Hot, warm & cold subscriber tiers"      },
  { to: "/behavioral-triggers",       label: "Behavioral Triggers",       desc: "Automate on opens, clicks & webhooks"   },
  { to: "/revenue-attribution",       label: "Revenue Attribution",       desc: "Track revenue from every campaign"      },
  { to: "/list-hygiene",              label: "List Hygiene",              desc: "Auto-unsubscribe inactive contacts"     },
  { to: "/prebuilt-campaigns",        label: "Pre-Built Campaigns",       desc: "20+ ready-to-send email sequences"      },
  { to: "/prebuilt-automations",      label: "Pre-Built Automations",     desc: "12+ install-and-activate workflows"     },
  { to: "/email-health-dashboard",   label: "Email Health Dashboard",    desc: "Monitor reputation & engagement"        },
  { to: "/smart-recommendations",    label: "Smart Recommendations",     desc: "AI-powered personalized suggestions"    },
  { to: "/ab-test-insights",         label: "A/B Test Insights",         desc: "Historical results & pattern detection" },
  { to: "/competitor-email-tracker",  label: "Competitor Tracker",        desc: "Monitor what competitors send"          },
  { to: "/landing-page-builder",     label: "Landing Page Builder",      desc: "Create opt-in pages that convert"       },
];

const FREE_TOOLS = [
  { to: "/spam-checker",          label: "Spam Score Checker",      desc: "Test any email for spam risk — free"     },
  { to: "/subject-line-tester",   label: "Subject Line Tester",     desc: "Score & get A/B variants instantly"      },
  { to: "/warmup-calculator",     label: "Warmup Calculator",       desc: "Generate a custom warmup schedule"       },
  { to: "/cold-email-generator",  label: "Cold Email Generator",    desc: "AI-written cold emails in seconds"       },
  { to: "/blacklist-checker",     label: "Blacklist Checker",       desc: "Check domain & IP against 20+ lists"     },
  { to: "/deliverability-grader", label: "Deliverability Grader",   desc: "Get an A–F grade for your domain"        },
  { to: "/roi-calculator",       label: "Email ROI Calculator",    desc: "See what your email list is worth"       },
  { to: "/email-signature-generator", label: "Signature Generator", desc: "Professional HTML signatures — free"   },
  { to: "/dmarc-checker",            label: "DMARC / SPF Checker",   desc: "Check email auth for any domain"      },
  { to: "/email-copy-analyzer",     label: "Email Copy Analyzer",   desc: "Score your email content instantly"   },
  { to: "/list-growth-calculator",  label: "List Growth Calculator", desc: "Project your list over 12 months"    },
  { to: "/newsletter-benchmark",   label: "Benchmark Report",       desc: "Compare your stats to industry avg"  },
];

export default function PublicLayout() {
  const [menuOpen, setMenuOpen]         = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [toolsOpen, setToolsOpen]       = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const toolsRef    = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close everything on route change
  useEffect(() => {
    setFeaturesOpen(false);
    setToolsOpen(false);
    setMenuOpen(false);
    setMobileAiOpen(false);
    setMobileToolsOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (featuresRef.current && !featuresRef.current.contains(e.target as Node)) {
        setFeaturesOpen(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const FEATURE_PATHS = new Set(AI_FEATURES.map((f) => f.to));
  const TOOL_PATHS = new Set(FREE_TOOLS.map((t) => t.to));
  const isFeaturesActive = location.pathname === "/features" || FEATURE_PATHS.has(location.pathname);
  const isToolsActive    = location.pathname === "/tools" || TOOL_PATHS.has(location.pathname);

  return (
    <div className="min-h-screen flex flex-col bg-paper font-body">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-md border-b border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-8 h-8 bg-coral rounded-lg flex items-center justify-center text-white font-display font-bold text-sm -rotate-2 shadow-md shadow-coral/30">
              M
            </div>
            <span className="font-display font-bold text-xl tracking-tight">MoreMail</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">

            {/* Features dropdown */}
            <div ref={featuresRef} className="relative">
              <button
                onClick={() => { setFeaturesOpen((o) => !o); setToolsOpen(false); }}
                className={clsx(
                  "flex items-center gap-1 text-sm font-medium transition-colors",
                  isFeaturesActive ? "text-ink" : "text-slate hover:text-ink"
                )}
              >
                Features
                <ChevronDown
                  size={14}
                  className={clsx("transition-transform duration-200", featuresOpen ? "rotate-180" : "")}
                />
              </button>

              {featuresOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[540px] bg-white rounded-2xl border border-black/[0.08] shadow-xl shadow-black/[0.08] overflow-hidden">
                  {/* Header row */}
                  <div className="px-4 py-3 border-b border-black/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-coral/10">
                        <Zap size={12} className="text-coral" />
                      </div>
                      <span className="text-xs font-bold text-ink uppercase tracking-wider">Features</span>
                    </div>
                    <Link
                      to="/features"
                      className="text-[11px] font-semibold text-coral hover:underline flex items-center gap-0.5"
                    >
                      All features <ArrowRight size={10} />
                    </Link>
                  </div>
                  {/* Feature links — 2-column grid */}
                  <div className="p-2 grid grid-cols-2 gap-x-1">
                    {AI_FEATURES.map((f) => (
                      <Link
                        key={f.to}
                        to={f.to}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-paper transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink group-hover:text-coral transition-colors leading-tight">
                            {f.label}
                          </p>
                          <p className="text-xs text-slate mt-0.5 leading-snug">{f.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Free Tools dropdown */}
            <div ref={toolsRef} className="relative">
              <button
                onClick={() => { setToolsOpen((o) => !o); setFeaturesOpen(false); }}
                className={clsx(
                  "flex items-center gap-1 text-sm font-medium transition-colors",
                  isToolsActive ? "text-ink" : "text-slate hover:text-ink"
                )}
              >
                Free Tools
                <ChevronDown
                  size={14}
                  className={clsx("transition-transform duration-200", toolsOpen ? "rotate-180" : "")}
                />
              </button>

              {toolsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[520px] bg-white rounded-2xl border border-black/[0.08] shadow-xl shadow-black/[0.08] overflow-hidden">
                  <div className="px-4 py-3 border-b border-black/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-teal/10">
                        <Zap size={12} className="text-teal" />
                      </div>
                      <span className="text-xs font-bold text-ink uppercase tracking-wider">Free Tools</span>
                    </div>
                    <Link to="/tools" className="text-[11px] font-semibold text-coral hover:underline flex items-center gap-0.5">
                      All tools <ArrowRight size={10} />
                    </Link>
                  </div>
                  <div className="p-2 grid grid-cols-2 gap-0.5">
                    {FREE_TOOLS.map((f) => (
                      <Link
                        key={f.to}
                        to={f.to}
                        className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl hover:bg-paper transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink group-hover:text-coral transition-colors leading-tight">
                            {f.label}
                          </p>
                          <p className="text-xs text-slate mt-0.5 leading-snug">{f.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/pricing"
              className={({ isActive }) =>
                clsx("text-sm font-medium transition-colors", isActive ? "text-ink" : "text-slate hover:text-ink")
              }>
              Pricing
            </NavLink>

            <NavLink to="/blog"
              className={({ isActive }) =>
                clsx("text-sm font-medium transition-colors", isActive ? "text-ink" : "text-slate hover:text-ink")
              }>
              Blog
            </NavLink>

            <NavLink to="/email-templates"
              className={({ isActive }) =>
                clsx("text-sm font-medium transition-colors", isActive ? "text-ink" : "text-slate hover:text-ink")
              }>
              Templates
            </NavLink>

            <NavLink to="/academy"
              className={({ isActive }) =>
                clsx("text-sm font-medium transition-colors", isActive ? "text-ink" : "text-slate hover:text-ink")
              }>
              Academy
            </NavLink>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-slate hover:text-ink transition-colors">
              Log in
            </Link>
            <Link to="/register"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/25">
              Start free <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-xl border border-black/[0.08] text-slate hover:text-ink hover:border-black/[0.14] transition-colors">
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-black/[0.06] bg-paper">
            <nav className="px-4 py-4 space-y-1">

              {/* Features accordion in mobile */}
              <div>
                <button
                  onClick={() => setMobileAiOpen((o) => !o)}
                  className={clsx(
                    "w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isFeaturesActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]"
                  )}
                >
                  Features
                  <ChevronDown size={14} className={clsx("transition-transform", mobileAiOpen ? "rotate-180" : "")} />
                </button>

                {mobileAiOpen && (
                  <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-coral/20 pl-3">
                    <Link
                      to="/features"
                      className="block px-3 py-2 rounded-lg text-xs font-bold text-slate hover:text-ink hover:bg-black/[0.03] transition-colors"
                    >
                      All features →
                    </Link>
                    {AI_FEATURES.map((f) => (
                      <Link
                        key={f.to}
                        to={f.to}
                        className="block px-3 py-2 rounded-lg text-sm text-slate hover:text-ink hover:bg-black/[0.03] transition-colors"
                      >
                        {f.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Free Tools accordion in mobile */}
              <div>
                <button
                  onClick={() => setMobileToolsOpen((o) => !o)}
                  className={clsx(
                    "w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isToolsActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]"
                  )}
                >
                  Free Tools
                  <ChevronDown size={14} className={clsx("transition-transform", mobileToolsOpen ? "rotate-180" : "")} />
                </button>

                {mobileToolsOpen && (
                  <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-teal/30 pl-3">
                    {FREE_TOOLS.slice(0, 3).map((f) => (
                      <Link
                        key={f.to}
                        to={f.to}
                        className="block px-3 py-2 rounded-lg text-sm text-slate hover:text-ink hover:bg-black/[0.03] transition-colors"
                      >
                        {f.label}
                      </Link>
                    ))}
                    <Link
                      to="/tools"
                      className="block px-3 py-2 rounded-lg text-xs font-bold text-coral hover:bg-black/[0.03] transition-colors"
                    >
                      See all {FREE_TOOLS.length} tools →
                    </Link>
                  </div>
                )}
              </div>

              <NavLink to="/pricing" onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx("flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]")
                }>
                Pricing
              </NavLink>

              <NavLink to="/blog" onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx("flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]")
                }>
                Blog
              </NavLink>

              <NavLink to="/email-templates" onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx("flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]")
                }>
                Templates
              </NavLink>

              <NavLink to="/academy" onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx("flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                    isActive ? "bg-coral/10 text-coral" : "text-slate hover:text-ink hover:bg-black/[0.03]")
                }>
                Academy
              </NavLink>

              <div className="pt-3 border-t border-black/[0.06] space-y-2 mt-3">
                <Link to="/login" onClick={() => setMenuOpen(false)}
                  className="block text-center px-4 py-3 rounded-xl text-sm font-semibold text-slate border border-black/[0.08] hover:border-black/[0.14]">
                  Log in
                </Link>
                <Link to="/register" onClick={() => setMenuOpen(false)}
                  className="block text-center px-4 py-3 rounded-xl bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
                  Start free — no card needed
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-8 lg:gap-10 mb-12">
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 bg-coral rounded-lg flex items-center justify-center text-white font-display font-bold text-sm -rotate-2">M</div>
                <span className="font-display font-bold text-lg tracking-tight">MoreMail</span>
              </div>
              <p className="text-sm text-white/50 leading-relaxed">AI-powered email marketing for teams that want results.</p>
            </div>

            {/* Product column */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Product</h4>
              <div className="space-y-2.5">
                <Link to="/features" className="block text-sm text-white/60 hover:text-white transition-colors">All Features</Link>
                <Link to="/ai-campaign-builder" className="block text-sm text-white/60 hover:text-white transition-colors">Campaign Builder</Link>
                <Link to="/ai-campaign-generator" className="block text-sm text-white/60 hover:text-white transition-colors">AI Campaign Generator</Link>
                <Link to="/subject-line-optimisation" className="block text-sm text-white/60 hover:text-white transition-colors">Subject Line AI</Link>
                <Link to="/auto-optimization" className="block text-sm text-white/60 hover:text-white transition-colors">Auto-Optimization</Link>
                <Link to="/send-time-optimization" className="block text-sm text-white/60 hover:text-white transition-colors">Send-Time Optimization</Link>
                <Link to="/spam-score" className="block text-sm text-white/60 hover:text-white transition-colors">Spam Score</Link>
                <Link to="/inbox-preview" className="block text-sm text-white/60 hover:text-white transition-colors">Inbox Preview</Link>
                <Link to="/engagement-scoring" className="block text-sm text-white/60 hover:text-white transition-colors">Engagement Scoring</Link>
                <Link to="/behavioral-triggers" className="block text-sm text-white/60 hover:text-white transition-colors">Behavioral Triggers</Link>
                <Link to="/revenue-attribution" className="block text-sm text-white/60 hover:text-white transition-colors">Revenue Attribution</Link>
                <Link to="/list-hygiene" className="block text-sm text-white/60 hover:text-white transition-colors">List Hygiene</Link>
                <Link to="/prebuilt-campaigns" className="block text-sm text-white/60 hover:text-white transition-colors">Pre-Built Campaigns</Link>
                <Link to="/prebuilt-automations" className="block text-sm text-white/60 hover:text-white transition-colors">Pre-Built Automations</Link>
                <Link to="/email-templates" className="block text-sm text-white/60 hover:text-white transition-colors">Email Templates</Link>
                <Link to="/integrations-directory" className="block text-sm text-white/60 hover:text-white transition-colors">Integrations</Link>
                <Link to="/academy" className="block text-sm text-white/60 hover:text-white transition-colors">Academy</Link>
                <Link to="/pricing" className="block text-sm text-white/60 hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="block text-sm text-white/60 hover:text-white transition-colors">Blog</Link>
              </div>
            </div>

            {/* Free Tools column */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Free Tools</h4>
              <div className="space-y-2.5">
                <Link to="/tools" className="block text-sm text-white/60 hover:text-white transition-colors font-semibold">All Free Tools</Link>
                {FREE_TOOLS.map(({ to, label }) => (
                  <Link key={to} to={to} className="block text-sm text-white/60 hover:text-white transition-colors">{label}</Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Use Cases</h4>
              <div className="space-y-2.5">
                {[["Welcome Emails","/welcome-email-sequences"],["Newsletters","/newsletter-platform"],["Drip Campaigns","/drip-email-campaigns"]].map(([l,h])=>(
                  <Link key={l} to={h} className="block text-sm text-white/60 hover:text-white transition-colors">{l}</Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Company</h4>
              <div className="space-y-2.5">
                {[["Privacy","#"],["Terms","#"],["Status","#"]].map(([l,h])=>(
                  <a key={l} href={h} className="block text-sm text-white/60 hover:text-white transition-colors">{l}</a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-white/[0.08] pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
            <span>© {new Date().getFullYear()} MoreMail, Inc. All rights reserved.</span>
            <span>Designed for email marketers who mean business.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
